/**
 * Lightweight metadata layer for the yoga studio.
 *
 * Kept separate from `yoga-data.ts` (which owns the practice scripts) so the
 * hub can offer navigation, filtering and "how are you arriving?" entry points
 * without touching the trauma-informed content itself.
 */

export type YogaLevel = "gentle" | "steady" | "deeper";
export type YogaPosition = "chair" | "floor" | "standing";

/**
 * Survival-response entry points — how someone arrives, not what they
 * "should" do. Nobody has to identify their response perfectly.
 */
export type YogaNeed = "fight" | "flight" | "freeze" | "fawn";

export type YogaMeta = {
  level: YogaLevel;
  position: YogaPosition;
  needs: YogaNeed[];
};

export const YOGA_LEVEL_LABELS: Record<YogaLevel, string> = {
  gentle: "Gentle",
  steady: "Steady",
  deeper: "Deeper",
};

export const YOGA_POSITION_LABELS: Record<YogaPosition, string> = {
  chair: "Chair / seated",
  floor: "Floor / lying",
  standing: "Standing",
};

/** Small labels shown on practice cards. */
export const YOGA_NEED_LABELS: Record<YogaNeed, string> = {
  fight: "Fight support",
  flight: "Flight support",
  freeze: "Freeze support",
  fawn: "Fawn support",
};

export const YOGA_NEED_EMOJI: Record<YogaNeed, string> = {
  fight: "🔥",
  flight: "🌪️",
  freeze: "❄️",
  fawn: "🤍",
};

export const YOGA_NEEDS: {
  id: YogaNeed;
  emoji: string;
  label: string;
  blurb: string;
  guidance: string;
}[] = [
  {
    id: "fight",
    emoji: "🔥",
    label: "Fight",
    blurb: "I feel angry, defensive, pressured or ready to react.",
    guidance:
      "Grounded strength, safe physical release and slower movements.",
  },
  {
    id: "flight",
    emoji: "🌪️",
    label: "Flight",
    blurb: "I feel restless, anxious, rushed or unable to settle.",
    guidance:
      "Rhythmic movement, steady stepping and gradually slowing down.",
  },
  {
    id: "freeze",
    emoji: "❄️",
    label: "Freeze",
    blurb: "I feel numb, stuck, heavy or disconnected.",
    guidance:
      "Orienting, tiny movements, chair support and gentle reconnection.",
  },
  {
    id: "fawn",
    emoji: "🤍",
    label: "Fawn",
    blurb:
      "I am focused on others, disconnected from my needs or finding it difficult to say no.",
    guidance:
      "Personal space, choice, boundaries and reconnecting with your own needs.",
  },
];

export const YOGA_NEEDS_NOTE =
  "You may recognise more than one response or move between them. You do not need to identify your response perfectly. Choose what feels most supportive right now.";

/** Suggested first practice for someone who has never opened the studio. */
export const YOGA_START_HERE_SLUG = "chair-yoga-for-safety";

export const YOGA_META: Record<string, YogaMeta> = {
  // Gentle Foundations
  "chair-yoga-for-safety": { level: "gentle", position: "chair", needs: ["freeze", "flight"] },
  "first-steps-into-yoga": { level: "gentle", position: "chair", needs: ["freeze"] },
  "breath-and-movement-basics": { level: "gentle", position: "chair", needs: ["flight"] },
  "gentle-stretch-and-release": { level: "gentle", position: "floor", needs: ["fight", "freeze"] },

  // Grounding & Safety
  "grounding-yoga-flow": { level: "gentle", position: "standing", needs: ["flight", "freeze"] },
  "rhythmic-grounding-flow": { level: "gentle", position: "standing", needs: ["flight", "freeze"] },
  "yoga-for-feeling-safe": { level: "steady", position: "floor", needs: ["freeze", "fawn"] },
  "grounding-strong-energy": { level: "steady", position: "standing", needs: ["fight"] },

  // Anxiety & Stress Relief
  "yoga-for-anxiety": { level: "gentle", position: "floor", needs: ["flight"] },
  "calm-the-nervous-system": { level: "gentle", position: "floor", needs: ["flight"] },
  "slow-the-rush": { level: "gentle", position: "standing", needs: ["flight"] },
  "rest-and-reset-flow": { level: "gentle", position: "floor", needs: ["flight", "fawn"] },

  // Trauma Recovery
  "somatic-yoga-for-trauma-recovery": { level: "steady", position: "floor", needs: ["freeze"] },
  "tiny-movements-to-reconnect": { level: "gentle", position: "chair", needs: ["freeze"] },
  "orient-and-return": { level: "gentle", position: "chair", needs: ["freeze", "flight"] },
  "building-capacity": { level: "steady", position: "standing", needs: ["freeze", "fight"] },
  "boundaries-through-movement": { level: "steady", position: "standing", needs: ["fawn", "fight"] },

  // Emotional Regulation
  "heart-opening-practice": { level: "gentle", position: "floor", needs: ["fawn", "freeze"] },
  "self-compassion-flow": { level: "gentle", position: "floor", needs: ["fawn"] },
  "choose-your-own-rhythm": { level: "gentle", position: "chair", needs: ["fawn"] },
  "finding-your-space": { level: "gentle", position: "standing", needs: ["fawn"] },

  // Sleep & Deep Rest
  "bedtime-yoga": { level: "gentle", position: "floor", needs: ["flight"] },
  "evening-wind-down": { level: "gentle", position: "floor", needs: ["flight"] },
  "restorative-yoga-for-stress-relief": { level: "gentle", position: "floor", needs: ["fawn", "flight"] },
  "yoga-nidra-for-deep-rest": { level: "gentle", position: "floor", needs: ["flight"] },
  "sleep-preparation-body-flow": { level: "gentle", position: "floor", needs: ["flight", "fight"] },

  // Movement & Release
  "full-body-release-flow": { level: "deeper", position: "standing", needs: ["fight"] },
  "shoulder-and-neck-release": { level: "gentle", position: "chair", needs: ["fight", "fawn"] },
  "hip-opening-for-emotional-release": { level: "deeper", position: "floor", needs: ["fight", "freeze"] },
  "step-sway-and-settle": { level: "gentle", position: "standing", needs: ["flight"] },
  "push-and-release": { level: "steady", position: "standing", needs: ["fight", "flight"] },

  // Recovery & Resilience
  "gentle-awakening": { level: "gentle", position: "chair", needs: ["freeze"] },
  "strong-feet-soft-body": { level: "steady", position: "standing", needs: ["fight", "freeze"] },
  "moving-with-confidence": { level: "steady", position: "standing", needs: ["fawn", "freeze"] },
  "recovery-flow": { level: "steady", position: "floor", needs: ["fawn", "flight"] },
};

const FALLBACK: YogaMeta = { level: "gentle", position: "floor", needs: [] };

export function getYogaMeta(slug: string): YogaMeta {
  return YOGA_META[slug] ?? FALLBACK;
}

/** "12 min" -> 12 */
export function parseMinutes(duration: string): number {
  const m = duration.match(/\d+/);
  return m ? Number(m[0]) : 0;
}

export type YogaTimeBucket = "short" | "medium" | "long";

export const YOGA_TIME_LABELS: Record<YogaTimeBucket, string> = {
  short: "Under 10 min",
  medium: "10–14 min",
  long: "15 min +",
};

export function timeBucket(duration: string): YogaTimeBucket {
  const mins = parseMinutes(duration);
  if (mins < 10) return "short";
  if (mins < 15) return "medium";
  return "long";
}
